import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import BufferOptionsItem from 'buffers/BufferOptionsItem';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import CreatePDFButton from 'molbio/maps/options/CreatePDFButton';
import {LinearSequenceOptionsProps} from 'molbio/maps/options/LinearSequenceOptionsView';
import OptionsBanner from 'molbio/maps/options/OptionsBanner';
import SearchButton from 'molbio/maps/options/SearchButton';
import {ZoomSlider} from 'molbio/maps/options/Sliders';
import useCollapsibleNavBar from 'molbio/maps/options/useCollapsibleNavBar';
import {NCBI_BLAST_MAX_LENGTH} from 'molbio/maps/selectables/Selection';
import {BENCHLING_BLAST_MENU_TITLE, NCBI_BLAST_MENU_TITLE} from 'search/blast/BLASTConstants';
import DisplayOptions from 'settings/visibility-options/DisplayOptions';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option, {OptionProps} from 'ui-library/menus/Option';

function getStrandedMenuOption(
  title: string,
  onSelect: (strand: StrandOfDoubleStranded) => void,
  onlyForward: boolean,
  submenuOptionProps: Partial<OptionProps<StrandOfDoubleStranded>> = {}
): JSX.Element {
  if (onlyForward) {
    return (
      <Option value={1} onSelect={onSelect}>
        {title}
      </Option>
    );
  } else {
    return (
      <Option>
        {title}
        <Menu>
          <Option {...submenuOptionProps} value={1} onSelect={onSelect}>
            Forward
          </Option>
          <Option {...submenuOptionProps} value={-1} onSelect={onSelect}>
            Reverse
          </Option>
        </Menu>
      </Option>
    );
  }
}

function getCreateSubmenu({
  createAnnotation,
  createPart,
  createPrimer,
  createProtein,
  createSequence,
  createTranslation,
  isCreateNewPartFromSelectionDisabled,
  isSingleStranded,
}: {
  createAnnotation: () => void;
  createPart: () => void;
  createPrimer: (strand: StrandOfDoubleStranded) => void;
  createProtein: (strand: StrandOfDoubleStranded) => void;
  createSequence: (newSequenceType?: SequenceType, strand?: StrandOfDoubleStranded) => void;
  createTranslation: (strand: StrandOfDoubleStranded) => void;
  isCreateNewPartFromSelectionDisabled: boolean;
  isSingleStranded: boolean;
}): JSX.Element {
  return (
    <Menu width={MenuWidth.SMALL}>
      <Option onSelect={createAnnotation}>Annotation</Option>
      {getStrandedMenuOption('Primer', createPrimer, false)}
      {getStrandedMenuOption('Translation', createTranslation, isSingleStranded)}
      {getStrandedMenuOption('New AA sequence', createProtein, isSingleStranded)}
      <Option
        onSelect={() => {
          createSequence(SequenceType.DOUBLE_STRANDED);
        }}
      >
        New DNA
      </Option>
      {getStrandedMenuOption(
        'New RNA',
        (strand: StrandOfDoubleStranded) => {
          createSequence(SequenceType.SINGLE_STRANDED, strand);
        },
        isSingleStranded
      )}
      <Option onSelect={createPart} isDisabled={isCreateNewPartFromSelectionDisabled}>
        New part
      </Option>
    </Menu>
  );
}

function getAnalyzeSubmenu({
  analyzeAsTranslation,
  handleOptimizeCodonsFromSelectionClick,
  isBenchlingBlastable,
  isNcbiBlastable,
  isRnaSequence,
  runBenchlingBlast,
  runNcbiBlast,
  runPrimer3,
  selectionLength,
}: {
  analyzeAsTranslation: () => void;
  handleOptimizeCodonsFromSelectionClick: (strand: StrandOfDoubleStranded) => void;
  isBenchlingBlastable: boolean;
  isNcbiBlastable: boolean;
  isRnaSequence: boolean;
  runBenchlingBlast: () => void;
  runNcbiBlast: () => void;
  runPrimer3: () => void;
  selectionLength: number;
}): JSX.Element {
  const selectionMultipleOf3 = selectionLength % 3 === 0;
  return (
    <Menu width={MenuWidth.SMALL}>
      <Option onSelect={runPrimer3}>Run Primer3</Option>
      {BenchlingConfig.ENABLE_BLAST_SEARCH && (
        <Option
          isDisabled={!isBenchlingBlastable}
          tooltipTitle={
            !isBenchlingBlastable
              ? 'The selected residues are too long or not eligible for BLAST.'
              : undefined
          }
          onSelect={runBenchlingBlast}
        >
          {BENCHLING_BLAST_MENU_TITLE}
        </Option>
      )}
      {BenchlingConfig.MOLBIO_ENABLE_EXTERNAL_BLAST && (
        <Option
          isDisabled={!isNcbiBlastable}
          tooltipTitle={
            !isNcbiBlastable ? `NCBI BLAST is limited to ${NCBI_BLAST_MAX_LENGTH} base pairs.` : undefined
          }
          onSelect={runNcbiBlast}
        >
          {NCBI_BLAST_MENU_TITLE}
        </Option>
      )}
      <Option onSelect={analyzeAsTranslation}>Analyze as translation</Option>
      {!isRnaSequence &&
        getStrandedMenuOption('Optimize codons', handleOptimizeCodonsFromSelectionClick, false, {
          isDisabled: !selectionMultipleOf3,
          tooltipTitle: selectionMultipleOf3 ? undefined : "Selection isn't a multiple of 3.",
        })}
    </Menu>
  );
}

enum ButtonKey {
  CREATE = 'create',
  ANALYZE = 'analyze',
  COPY = 'copy',
  PDF = 'pdf',
  SEARCH = 'search',
}

export default function LinearOptions(props: LinearSequenceOptionsProps): JSX.Element {
  const {
    isOligo,
    zoomSliderProps,
    bannerText,
    selectionLength,
    isCopyable,
    onCopy,
    onOpenPreview,
    visibilityOptions,
    featureCounts,
    onToggleSearch,
    allowFeatureEdits,
    onClickCreate,
    onClickAnalyze,
  } = props;

  let uncollapsibleWidth = 38; // Width of the visibility options
  if (!isOligo) {
    uncollapsibleWidth += 110; // Width of the zoom slider
  }
  if (bannerText != null) {
    uncollapsibleWidth += 10; // The banner text collapses, but its margin does not
  }

  const emptySelection = selectionLength === 0;
  const {ref, hiddenStatusByElementKey} = useCollapsibleNavBar<ButtonKey>({
    uncollapsibleWidth,
    dropdownWidth: 24,
    collapsibleElements: [
      ...(!isOligo && allowFeatureEdits
        ? [
            {key: ButtonKey.CREATE, width: 75, priority: 2},
            {key: ButtonKey.ANALYZE, width: 82, priority: 3},
          ]
        : []),
      {key: ButtonKey.COPY, width: 57, priority: 4},
      {key: ButtonKey.PDF, width: 92, priority: 5},
      {key: ButtonKey.SEARCH, width: 24, priority: 1},
    ],
  });

  const collapsedButtonMenuItems = [
    <Option isDisabled={emptySelection} key={ButtonKey.CREATE} onHover={onClickCreate}>
      Create
      {getCreateSubmenu(props)}
    </Option>,
    <Option isDisabled={emptySelection} key={ButtonKey.ANALYZE} onHover={onClickAnalyze}>
      Analyze
      {getAnalyzeSubmenu(props)}
    </Option>,
    <Option isDisabled={!isCopyable} onSelect={onCopy} key={ButtonKey.COPY}>
      Copy
    </Option>,
    <Option onSelect={onOpenPreview} key={ButtonKey.PDF}>
      Create PDF
    </Option>,
    <Option onSelect={onToggleSearch} key={ButtonKey.SEARCH}>
      Search
    </Option>,
  ].filter((item) => hiddenStatusByElementKey[item.key as ButtonKey]);

  return (
    <div
      className="buffer-options-container"
      {...getTestProps({component: 'LinearOptions', element: 'root'})}
      ref={ref}
    >
      <BufferOptionsItem>{!isOligo && <ZoomSlider {...zoomSliderProps} />}</BufferOptionsItem>
      <OptionsBanner bannerText={bannerText} />
      <BufferOptionsItem className="u-vertical-center-items">
        <Tooltip
          title="Select bases to create features, analyze, or copy."
          placement={TooltipPlacement.BOTTOM}
          disabled={!emptySelection}
        >
          <ButtonGroup>
            {!isOligo && allowFeatureEdits && (
              <>
                {!hiddenStatusByElementKey[ButtonKey.CREATE] && (
                  <Dropdown menu={getCreateSubmenu(props)}>
                    <Button
                      buttonStyle={ButtonStyle.SECONDARY}
                      isDropdown={true}
                      text="Create"
                      size={ButtonSize.MINI}
                      disabled={emptySelection}
                      onMouseDown={props.onClickCreate}
                    />
                  </Dropdown>
                )}
                {!hiddenStatusByElementKey[ButtonKey.ANALYZE] && (
                  <Dropdown menu={getAnalyzeSubmenu(props)}>
                    <Button
                      buttonStyle={ButtonStyle.SECONDARY}
                      isDropdown={true}
                      text="Analyze"
                      size={ButtonSize.MINI}
                      disabled={emptySelection}
                      onMouseDown={props.onClickAnalyze}
                    />
                  </Dropdown>
                )}
              </>
            )}
            {!hiddenStatusByElementKey[ButtonKey.COPY] && (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                text="Copy"
                onClick={onCopy}
                disabled={!isCopyable}
                dataTestAttrs={{component: 'LinearOptions', element: 'button', key: 'copy'}}
              />
            )}
          </ButtonGroup>
        </Tooltip>
        <ButtonGroup>
          {!hiddenStatusByElementKey[ButtonKey.PDF] && <CreatePDFButton onOpenPreview={onOpenPreview} />}
        </ButtonGroup>
        <ButtonGroup>
          {collapsedButtonMenuItems.length > 0 && (
            <Dropdown
              menu={
                <Menu width={MenuWidth.SMALL} dropLeft={true}>
                  {collapsedButtonMenuItems}
                </Menu>
              }
            >
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="ellipsis-vertical"
              />
            </Dropdown>
          )}
        </ButtonGroup>
        <ButtonGroup>
          {/* TODO: Could also collapse these once modernized */}
          <DisplayOptions dropLeft={true} options={visibilityOptions} featureCounts={featureCounts} />
          {!hiddenStatusByElementKey[ButtonKey.SEARCH] && <SearchButton onToggleSearch={onToggleSearch} />}
        </ButtonGroup>
      </BufferOptionsItem>
    </div>
  );
}
